/**
 * Comment thread action hooks for document state management
 *
 * These hooks call direct named methods on the Zustand DocumentStore,
 * replacing the previous dispatch(actionCreator(...)) pattern.
 *
 * The direct store methods handle:
 * 1. Injecting currentUser into actions
 * 2. Applying mutations via withHistoryUpdate
 * 3. Notifying action listeners (ActionRecordService, DocumentSaveService, PatchHandlerService)
 */
import type {ICommentThreadData} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/types";

/**
 * Hook to create a comment thread
 *
 * Calls the direct createCommentThread method on the Zustand store.
 *
 * @returns A function to create a comment thread
 */
export function useCreateCommentThread(): (commentThread: ICommentThreadData) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThread: ICommentThreadData) => {
            documentStore.getState().createCommentThread(commentThread);
        },
        [documentStore],
    );
}

/**
 * Hook to delete a comment thread
 *
 * Calls the direct deleteCommentThread method on the Zustand store.
 *
 * @returns A function to delete a comment thread by its UID
 */
export function useDeleteCommentThread(): (commentThreadUid: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThreadUid: string) => {
            documentStore.getState().deleteCommentThread(commentThreadUid);
        },
        [documentStore],
    );
}

/**
 * Hook to pin/unpin a comment thread
 *
 * Calls the direct pinCommentThread method on the Zustand store.
 *
 * @returns A function to pin or unpin a comment thread
 */
export function usePinCommentThread(): (commentThreadUid: string, pinned: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThreadUid: string, pinned: boolean) => {
            documentStore.getState().pinCommentThread(commentThreadUid, pinned);
        },
        [documentStore],
    );
}

/**
 * Hook to resolve a comment thread
 *
 * Calls the direct resolveCommentThread method on the Zustand store.
 *
 * @returns A function to resolve a comment thread by its UID
 */
export function useResolveCommentThread(): (commentThreadUid: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThreadUid: string) => {
            documentStore.getState().resolveCommentThread(commentThreadUid);
        },
        [documentStore],
    );
}

/**
 * Hook to unresolve a comment thread
 *
 * Calls the direct unresolveCommentThread method on the Zustand store.
 *
 * @returns A function to unresolve a comment thread by its UID
 */
export function useUnresolveCommentThread(): (commentThreadUid: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThreadUid: string) => {
            documentStore.getState().unresolveCommentThread(commentThreadUid);
        },
        [documentStore],
    );
}

/**
 * Hook to increment the comment count of a thread
 *
 * Calls the direct incrementCommentThreadCommentCount method on the Zustand store.
 *
 * @returns A function to increment the comment count of a thread
 */
export function useIncrementCommentThreadCommentCount(): (commentThreadUid: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThreadUid: string) => {
            documentStore.getState().incrementCommentThreadCommentCount(commentThreadUid);
        },
        [documentStore],
    );
}

/**
 * Hook to update comment thread position anchors
 *
 * Calls the direct updateCommentThreadsPositionAnchor method on the Zustand store.
 *
 * @returns A function to update comment thread position anchors
 */
export function useUpdateCommentThreadsPositionAnchor(): (commentThreads: ICommentThreadData[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (commentThreads: ICommentThreadData[]) => {
            documentStore.getState().updateCommentThreadsPositionAnchor(commentThreads);
        },
        [documentStore],
    );
}

/**
 * Factory function to create comment thread actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to perform comment thread actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with comment thread action methods
 */
export function createCommentThreadActions(documentStore: DocumentStore) {
    return {
        createCommentThread: (commentThread: ICommentThreadData) => {
            documentStore.getState().createCommentThread(commentThread);
        },
        deleteCommentThread: (commentThreadUid: string) => {
            documentStore.getState().deleteCommentThread(commentThreadUid);
        },
        pinCommentThread: (commentThreadUid: string, pinned: boolean) => {
            documentStore.getState().pinCommentThread(commentThreadUid, pinned);
        },
        resolveCommentThread: (commentThreadUid: string) => {
            documentStore.getState().resolveCommentThread(commentThreadUid);
        },
        unresolveCommentThread: (commentThreadUid: string) => {
            documentStore.getState().unresolveCommentThread(commentThreadUid);
        },
        incrementCommentThreadCommentCount: (commentThreadUid: string) => {
            documentStore.getState().incrementCommentThreadCommentCount(commentThreadUid);
        },
        updateCommentThreadsPositionAnchor: (commentThreads: ICommentThreadData[]) => {
            documentStore.getState().updateCommentThreadsPositionAnchor(commentThreads);
        },
    };
}
